import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface RoutineConfig {
  activo: boolean;
  hora_recordatorio: string;
  whatsapp: string | null;
  numero_reserva: string | null;
  numero_reserva_activo: boolean;
  dias_objetivo: number;
}

interface StreakStats {
  racha_actual: number;
  racha_maxima: number;
  dias_activos_mes: number;
  dias_activos_semana: number;
}

interface CalendarDay {
  fecha: string;
  completado: boolean;
  total_partidas: number;
}

interface DayProgress {
  id: number;
  juego_nombre: string;
  tipo_nivel: string;
  dificultad_nombre: string;
  nivel_id: number;
  tablero_id: number;
  tiempo: number;
  movimientos: number;
  fecha_completado: string;
}

export default function RoutinePage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<RoutineConfig>({
    activo: false,
    hora_recordatorio: '09:00',
    whatsapp: '',
    numero_reserva: '',
    numero_reserva_activo: false,
    dias_objetivo: 5,
  });
  const [streakStats, setStreakStats] = useState<StreakStats | null>(null);
  const [calendar, setCalendar] = useState<CalendarDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dayProgress, setDayProgress] = useState<DayProgress[]>([]);
  const [loadingDayProgress, setLoadingDayProgress] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      let hasError = false;

      // Cargar configuración
      try {
        const configRes = await api.get('/routine/config');
        if (configRes.data.data) {
          setConfig({
            activo: configRes.data.data.activo,
            hora_recordatorio: configRes.data.data.hora_recordatorio.substring(0, 5), // HH:MM
            whatsapp: configRes.data.data.whatsapp || '',
            numero_reserva: configRes.data.data.numero_reserva || '',
            numero_reserva_activo: configRes.data.data.numero_reserva_activo,
            dias_objetivo: configRes.data.data.dias_objetivo,
          });
        }
      } catch (err) {
        console.error('Error cargando config:', err);
        hasError = true;
      }

      // Cargar estadísticas de racha
      try {
        const streakRes = await api.get('/routine/streak');
        setStreakStats(streakRes.data.data);
      } catch (err) {
        console.error('Error cargando streak:', err);
        hasError = true;
      }

      // Cargar calendario (últimos 30 días)
      try {
        const calendarRes = await api.get('/routine/calendar?days=30');
        setCalendar(calendarRes.data.data);
      } catch (err) {
        console.error('Error cargando calendar:', err);
        hasError = true;
      }

      if (hasError) {
        showMessage('error', 'Algunos datos no se pudieron cargar. Revisa la consola.');
      }
    } catch (error) {
      console.error('Error general cargando datos de rutina:', error);
      showMessage('error', 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validaciones
    if (config.activo && !config.whatsapp) {
      showMessage('error', 'Debes proporcionar un número de WhatsApp para activar los recordatorios');
      return;
    }

    if (config.numero_reserva_activo && !config.numero_reserva) {
      showMessage('error', 'Debes proporcionar un número de reserva si está activado');
      return;
    }

    try {
      setSaving(true);
      await api.post('/routine/config', config);
      showMessage('success', '¡Configuración guardada correctamente!');
      loadData(); // Recargar
    } catch (error) {
      console.error('Error guardando configuración:', error);
      showMessage('error', 'Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const getDayColor = (day: CalendarDay | undefined) => {
    if (!day) return 'bg-gray-700';
    if (day.completado) return 'bg-green-500';
    if (day.total_partidas > 0) return 'bg-yellow-500';
    return 'bg-gray-700';
  };

  const loadDayProgress = async (fecha: string) => {
    try {
      setLoadingDayProgress(true);
      setSelectedDate(fecha);

      // Llamar al endpoint de progreso con filtro por fecha
      const response = await api.get(`/progress?fecha=${fecha}`);
      setDayProgress(response.data.data || []);
    } catch (error) {
      console.error('Error cargando progreso del día:', error);
      setDayProgress([]);
    } finally {
      setLoadingDayProgress(false);
    }
  };

  const formatTime = (seconds: number) => {
    if (!seconds) return '-';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDayClick = (day: CalendarDay | undefined) => {
    if (!day || day.total_partidas === 0) return;
    loadDayProgress(day.fecha);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-4xl font-bold text-white">📅 Mi Rutina Diaria</h1>
            <button
              onClick={() => navigate('/progreso')}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-semibold transition-all"
            >
              📊 Ver Progreso
            </button>
          </div>
          <p className="text-white/70">Establece recordatorios para mantener tu entrenamiento cognitivo constante</p>
        </div>

        {/* Mensajes */}
        {message && (
          <div
            className={`mb-6 p-4 rounded-xl ${
              message.type === 'success' ? 'bg-green-500/20 border-2 border-green-500' : 'bg-red-500/20 border-2 border-red-500'
            }`}
          >
            <div className="text-white font-semibold">
              {message.type === 'success' ? '✓' : '⚠'} {message.text}
            </div>
          </div>
        )}

        {/* Estadísticas de racha */}
        {streakStats && (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-white mb-4">🔥 Racha y Actividad</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/20 rounded-xl p-4 text-center">
                <div className="text-sm text-white/70 mb-1">Racha actual</div>
                <div className="text-3xl font-bold text-white">{streakStats.racha_actual} 🔥</div>
              </div>
              <div className="bg-white/20 rounded-xl p-4 text-center">
                <div className="text-sm text-white/70 mb-1">Racha máxima</div>
                <div className="text-3xl font-bold text-white">{streakStats.racha_maxima}</div>
              </div>
              <div className="bg-white/20 rounded-xl p-4 text-center">
                <div className="text-sm text-white/70 mb-1">Días esta semana</div>
                <div className="text-3xl font-bold text-white">{streakStats.dias_activos_semana}</div>
              </div>
              <div className="bg-white/20 rounded-xl p-4 text-center">
                <div className="text-sm text-white/70 mb-1">Días este mes</div>
                <div className="text-3xl font-bold text-white">{streakStats.dias_activos_mes}</div>
              </div>
            </div>
          </div>
        )}

        {/* Calendario de actividad */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-white mb-4">📆 Últimos 30 días</h2>
          <p className="text-white/60 text-sm mb-3">Haz clic en un día para ver las partidas</p>
          <div className="grid grid-cols-10 gap-2">
            {calendar.slice(0, 30).map((day, index) => (
              <div
                key={index}
                className={`aspect-square rounded-lg ${getDayColor(day)} transition-all hover:scale-110 ${
                  day?.total_partidas > 0 ? 'cursor-pointer' : 'cursor-default'
                }`}
                title={`${day?.fecha || '-'}: ${day?.total_partidas || 0} partidas`}
                onClick={() => handleDayClick(day)}
              />
            ))}
          </div>
          <div className="flex items-center gap-4 mt-4 text-sm text-white/70">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span>Completado</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-500 rounded"></div>
              <span>Parcial</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-700 rounded"></div>
              <span>Sin actividad</span>
            </div>
          </div>
        </div>

        {/* Configuración */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-white mb-4">⚙️ Configuración</h2>

          <div className="bg-blue-500/20 border-l-4 border-blue-500 p-4 rounded-lg mb-6">
            <div className="flex items-start gap-3">
              <span className="text-2xl">ℹ️</span>
              <div className="text-white/90 text-sm">
                <strong>¿Por qué una rutina diaria?</strong><br />
                El entrenamiento cognitivo es más efectivo cuando se practica de forma constante.
                Te ayudaremos a mantener el hábito con recordatorios personalizados.
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Activar recordatorios */}
            <div className="flex items-center justify-between">
              <label className="text-white font-semibold">Activar recordatorios por WhatsApp</label>
              <button
                onClick={() => setConfig({ ...config, activo: !config.activo })}
                className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
                  config.activo ? 'bg-green-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                    config.activo ? 'translate-x-9' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Número WhatsApp */}
            <div>
              <label className="block text-white font-semibold mb-2">📱 Número de WhatsApp</label>
              <input
                type="tel"
                value={config.whatsapp || ''}
                onChange={(e) => setConfig({ ...config, whatsapp: e.target.value })}
                placeholder="+34 600 123 456"
                className="w-full px-4 py-3 bg-white/20 text-white rounded-lg border-2 border-white/30 focus:outline-none focus:border-white/50 placeholder-white/50"
                required={config.activo}
              />
              <p className="text-white/60 text-sm mt-1">Incluye el código de país (ej: +34 para España)</p>
            </div>

            {/* Número de reserva */}
            <div className="flex items-center justify-between">
              <label className="text-white font-semibold">Activar número de reserva</label>
              <button
                onClick={() => setConfig({ ...config, numero_reserva_activo: !config.numero_reserva_activo })}
                className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
                  config.numero_reserva_activo ? 'bg-green-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                    config.numero_reserva_activo ? 'translate-x-9' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {config.numero_reserva_activo && (
              <div>
                <label className="block text-white font-semibold mb-2">📞 Número de reserva</label>
                <input
                  type="tel"
                  value={config.numero_reserva || ''}
                  onChange={(e) => setConfig({ ...config, numero_reserva: e.target.value })}
                  placeholder="+34 600 123 456"
                  className="w-full px-4 py-3 bg-white/20 text-white rounded-lg border-2 border-white/30 focus:outline-none focus:border-white/50 placeholder-white/50"
                  required={config.numero_reserva_activo}
                />
                <p className="text-white/60 text-sm mt-1">Número alternativo para cuando el principal no esté disponible</p>
              </div>
            )}

            {/* Hora de recordatorio */}
            <div>
              <label className="block text-white font-semibold mb-2">🕐 Hora del recordatorio</label>
              <input
                type="time"
                value={config.hora_recordatorio}
                onChange={(e) => setConfig({ ...config, hora_recordatorio: e.target.value })}
                className="w-full px-4 py-3 bg-white/20 text-white rounded-lg border-2 border-white/30 focus:outline-none focus:border-white/50"
                required
              />
              <p className="text-white/60 text-sm mt-1">¿A qué hora prefieres recibir el recordatorio?</p>
            </div>

            {/* Días objetivo */}
            <div>
              <label className="block text-white font-semibold mb-2">🎯 Días de entrenamiento por semana</label>
              <select
                value={config.dias_objetivo}
                onChange={(e) => setConfig({ ...config, dias_objetivo: parseInt(e.target.value) })}
                className="w-full px-4 py-3 bg-white/20 text-white rounded-lg border-2 border-white/30 focus:outline-none focus:border-white/50 [&>option]:bg-purple-900 [&>option]:text-white"
              >
                <option value={3}>3 días</option>
                <option value={4}>4 días</option>
                <option value={5}>5 días (recomendado)</option>
                <option value={6}>6 días</option>
                <option value={7}>7 días</option>
              </select>
            </div>

            {/* Botones */}
            <div className="flex gap-4 pt-4">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-bold transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? '💾 Guardando...' : '💾 Guardar Configuración'}
              </button>
              <button
                onClick={() => navigate('/juegos')}
                className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white rounded-xl font-bold transition-all"
              >
                Volver
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de partidas del día */}
      {selectedDate && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedDate(null)}
        >
          <div
            className="bg-gradient-to-br from-purple-900 to-indigo-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-white">
                  📅 Partidas del {new Date(selectedDate + 'T00:00:00').toLocaleDateString('es-ES', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </h3>
                <button
                  onClick={() => setSelectedDate(null)}
                  className="text-white/70 hover:text-white text-2xl"
                >
                  ✕
                </button>
              </div>

              {loadingDayProgress ? (
                <div className="text-white text-center py-8">🔄 Cargando...</div>
              ) : dayProgress.length === 0 ? (
                <div className="text-white/70 text-center py-8">No hay partidas este día</div>
              ) : (
                <div className="space-y-3">
                  {dayProgress.map((progress) => (
                    <div
                      key={progress.id}
                      className="bg-white/10 backdrop-blur-lg rounded-xl p-4 hover:bg-white/15 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-white font-bold text-lg">
                              {progress.juego_nombre || progress.tipo_nivel}
                            </span>
                            <span className="text-white/60 text-sm">
                              {progress.dificultad_nombre || `Nivel ${progress.nivel_id}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-white/70">
                            {progress.tablero_id && (
                              <span>📋 #{progress.tablero_id}</span>
                            )}
                            <span>⏱️ {formatTime(progress.tiempo)}</span>
                            <span>🎯 {progress.movimientos} movs</span>
                            <span className="text-xs">
                              {new Date(progress.fecha_completado).toLocaleTimeString('es-ES', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>
                        {progress.tablero_id && (
                          <button
                            onClick={() => {
                              const gameCode = progress.tipo_nivel;
                              const nivelId = progress.nivel_id;
                              const tableroId = progress.tablero_id;

                              let url = `/juegos/${gameCode}?dificultad=${nivelId}`;

                              if (['bombillas', 'binary', 'hitori', 'kakurasu'].includes(gameCode)) {
                                url += `&seed=${tableroId}`;
                              } else {
                                url += `&tableroId=${tableroId}`;
                              }

                              navigate(url);
                            }}
                            className="px-4 py-2 bg-blue-500/80 hover:bg-blue-600 text-white rounded-lg text-sm font-semibold transition-all"
                          >
                            Ir →
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 text-center">
                <button
                  onClick={() => setSelectedDate(null)}
                  className="px-6 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-semibold transition-all"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
